import React, { useEffect, useState } from 'react';
import { Table, Input, Button, DatePicker, Form } from 'antd';
import dayjs from 'dayjs';
import { supabase } from '../../supabaseClient';

// 获取用户邮箱的辅助函数
async function fetchUserEmails(userIds: string[]): Promise<Record<string, string>> {
  if (!userIds.length) return {};
  const { data, error } = await supabase
    .from('u_profiles')
    .select('user_id, email')
    .in('user_id', userIds);
  if (error) return {};
  const map: Record<string, string> = {};
  data.forEach((item: any) => { map[item.user_id] = item.email; });
  return map;
}

const UserWithdrawList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [userEmailMap, setUserEmailMap] = useState<Record<string, string>>({});

  const fetchData = async (filters?: any, page = 1, pageSize = 15) => {
    setLoading(true);
    let userIdList: string[] | undefined = undefined;
    if (filters && filters.email) {
      // 先查邮箱对应的user_id
      const { data: emailUsers } = await supabase
        .from('u_profiles')
        .select('user_id')
        .ilike('email', `%${filters.email}%`);
      userIdList = (emailUsers || []).map((item: any) => item.user_id);
      if (userIdList.length === 0) {
        setData([]);
        setPagination({ current: page, pageSize, total: 0 });
        setLoading(false);
        return;
      }
    }
    let query = supabase.from('u_withdraws').select('*', { count: 'exact' }).order('created_at', { ascending: false });
    if (filters) {
      if (filters.user_id) query = query.eq('user_id', filters.user_id);
      if (userIdList) query = query.in('user_id', userIdList);
      if (filters.address) query = query.ilike('address', `%${filters.address}%`);
      if (filters.tx_hash) query = query.ilike('tx_hash', `%${filters.tx_hash}%`);
      if (filters.start && filters.end) query = query.gte('created_at', filters.start).lte('created_at', filters.end);
    }
    query = query.range((page - 1) * pageSize, page * pageSize - 1);
    const { data: list, count } = await query;
    // 查询邮箱
    const userIds = (list || []).map((item: any) => item.user_id);
    let emailMap: Record<string, string> = {};
    if (userIds.length) {
      emailMap = await fetchUserEmails(userIds);
    }
    setUserEmailMap(emailMap);
    setData(list || []);
    setPagination({ current: page, pageSize, total: count || 0 });
    setLoading(false);
  };

  const handleFilter = async () => {
    const values = await form.validateFields();
    let start = dateRange && dateRange[0] ? dateRange[0] : undefined;
    let end = dateRange && dateRange[1] ? dateRange[1] : undefined;
    await fetchData({
      user_id: values.user_id || undefined,
      email: values.email || undefined,
      address: values.address || undefined,
      tx_hash: values.tx_hash || undefined,
      start,
      end,
    }, 1, pagination.pageSize);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTableChange = (pag: any) => {
    fetchData(form.getFieldsValue(), pag.current, pag.pageSize);
  };

  const columns = [
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 100, render: (text: string) => text ? new Date(text).toLocaleString('zh-CN', { hour12: false }) : '' },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 120 },
    { title: '用户邮箱', dataIndex: 'email', key: 'email', width: 120, render: (_: any, record: any) => userEmailMap[record.user_id] || '' },
    { title: '地址', dataIndex: 'address', key: 'address', width: 150 },
    { title: '数量', dataIndex: 'amount', key: 'amount', width: 60 },
    { title: '单位', dataIndex: 'token_symbol', key: 'token_symbol', width: 50 },
    { title: '备注', dataIndex: 'remark', key: 'remark', width: 60 },
    { title: 'TxHash', dataIndex: 'tx_hash', key: 'tx_hash', width: 220, render: (text: string) => text ? <a href={`https://snowtrace.io/tx/${text}`} target="_blank" rel="noopener noreferrer">{text}</a> : '' },
    { title: '状态', dataIndex: 'status', key: 'status', width: 60, render: (text: string) => {
      if (text === '1') return <span style={{color: '#52c41a'}}>提现成功</span>;
      if (text === '2') return <span style={{color: '#ff4d4f'}}>提现失败</span>;
      if (text === '0') return <span style={{color: '#faad14'}}>申请中</span>;
      return text || '-';
    } },
  ];

  return (
    <div style={{ overflowX: 'auto', background: '#fff', padding: 12 }}>
      <Form form={form} layout="inline" style={{ marginBottom: 16, flexWrap: 'nowrap', alignItems: 'center' }}>
        <Form.Item name="user_id">
          <Input placeholder="用户ID" allowClear style={{ width: 220 }} />
        </Form.Item>
        <Form.Item name="email">
          <Input placeholder="用户邮箱" allowClear style={{ width: 220 }} />
        </Form.Item>
        <Form.Item name="address">
          <Input placeholder="地址" allowClear style={{ width: 220 }} />
        </Form.Item>
        <Form.Item name="tx_hash">
          <Input placeholder="TxHash" allowClear style={{ width: 220 }} />
        </Form.Item>
        <Form.Item name="created_at" style={{ marginBottom: 0 }}>
          <DatePicker.RangePicker
            style={{ width: 240 }}
            placeholder={['开始时间', '结束时间']}
            value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
            onChange={dates => setDateRange(dates && dates[0] && dates[1] ? [dates[0].toISOString(), dates[1].toISOString()] : null)}
            allowClear
            showTime={false}
          />
        </Form.Item>
        <Button type="primary" onClick={handleFilter} loading={loading} style={{ marginLeft: 8 }}>筛选</Button>
        <Button style={{ marginLeft: 8 }} onClick={() => { form.resetFields(); setDateRange(null); fetchData(); }}>重置</Button>
      </Form>
      <Table
        rowKey="uuid"
        columns={columns}
        dataSource={data}
        loading={loading}
        bordered
        pagination={{ ...pagination, showSizeChanger: false, showTotal: (total) => `共 ${total} 条数据`, position: ['bottomRight'] }}
        scroll={{ x: 1200 }}
        size="small"
        style={{ minWidth: 800 }}
        tableLayout="fixed"
        onChange={handleTableChange}
      />
    </div>
  );
};

export default UserWithdrawList;

